/**
 * The single funnel every person->device SYNC must report through.
 *
 * Two separate backend records describe a sync, and the direct/LAN path used to write
 * only one of them:
 *
 *   PATCH .../mark-synced        -> device_audit_logs + person_device_enrollments
 *   POST  .../credential-outcome -> device_credential_states (the reconciliation ledger)
 *
 * The REVOKE path reported both (utils/deviceRevoke.ts). The sync path called only
 * mark-synced, so "Histórico de Sincronização" showed Sucesso while every state column
 * in the UI still read "Sem credencial" / "Não confirmado" — and a FAILED sync was
 * reported nowhere at all, leaving the reconciliation dashboard blind to a resident
 * missing a face on eight readers.
 *
 * Keeping both calls behind one function is the point: they cannot drift apart again.
 */

import { fetchWithAuth } from '../services/api';
import { announceDeviceCredentialChange } from '../services/deviceCredentialAnnounce';

/** Shape of whatever device object the call site happens to hold. */
export interface DeviceLike {
  device_type?: string | null;
  vendor?: string | null;
}

export interface DeviceSyncOutcomeInput {
  customerId: string;
  personId: string;
  deviceId: string;
  /** Used to pick the credential type; omit for the face default. */
  device?: DeviceLike;
  success: boolean;
  /** Where the write actually happened — 'local' | 'public' | 'cloud'. */
  source?: string;
  durationMs?: number;
  /**
   * The RAW device error. Deliberately not humanised: the ledger is a diagnostic
   * record, and `humanizeDeviceSyncError` is for toasts only.
   */
  errorMessage?: string | null;
  /** Explicit override when the caller knows better than the device type. */
  credentialType?: string;
  /** True only when the credential was re-read from the device after writing. */
  readbackVerified?: boolean;
  /**
   * Set false ONLY when the backend already recorded this outcome in
   * device_credential_states — i.e. the cloud sync path, which gets its result first-hand
   * from iot-connect. Re-reporting it from the browser would overwrite a device-level
   * result with our second-hand summary. Direct/LAN syncs must leave this true.
   */
  recordLedger?: boolean;
}

const RFID_DEVICE_TYPES = new Set(['rfid_reader', 'controlid_rfid']);
const FINGERPRINT_DEVICE_TYPES = new Set(['fingerprint_scanner']);

/**
 * Which credential a sync to this device actually writes. The ledger keys on
 * (person, device, credential_type), so reporting everything as 'face' would file an RFID
 * reader's or a fingerprint reader's outcome under a credential it never received — and
 * leave the real one unaccounted for.
 *
 * Note a ControlID iDFlex reports a generic device_type, so callers that already know
 * they are talking to a fingerprint reader should pass `credentialType` explicitly.
 */
export function credentialTypeForDevice(device?: DeviceLike | null): string {
  const deviceType = (device?.device_type ?? '').toLowerCase();
  if (RFID_DEVICE_TYPES.has(deviceType)) return 'rfid_card';
  if (FINGERPRINT_DEVICE_TYPES.has(deviceType)) return 'fingerprint';
  if (device?.vendor === 'rfid') return 'rfid_card';
  return 'face';
}

/**
 * `source` describes how much we trust the write: a direct LAN/DDNS call is first-hand,
 * a cloud relay is second-hand. 'public' is still the Electron client talking straight
 * to the device (over DDNS), so it counts as direct.
 */
function outcomeSourceFor(source?: string): string {
  return source === 'cloud' ? 'electron_hybrid_cloud_fallback' : 'electron_hybrid_direct';
}

/**
 * Reports one device's sync result. Always records the credential outcome (success OR
 * failure); logs the audit/enrollment row only on success.
 *
 * Never throws and never rejects — reporting is telemetry and must not take down a sync
 * loop or surface a toast. Failures are intentionally swallowed, not retried here.
 */
export async function reportDeviceSyncOutcome(input: DeviceSyncOutcomeInput): Promise<void> {
  const { customerId, personId, deviceId } = input;
  if (!customerId || !personId || !deviceId) return;

  const base = `/api/v1/customers/${customerId}/people/${personId}/devices/${deviceId}`;
  const jsonHeaders = { 'Content-Type': 'application/json' };

  const outcomeBody: Record<string, unknown> = {
    credential_type: input.credentialType ?? credentialTypeForDevice(input.device),
    operation: 'grant',
    outcome: input.success ? 'confirmed_present' : 'failed',
    source: outcomeSourceFor(input.source),
    error_message: input.errorMessage ?? undefined,
    readback_verified: input.readbackVerified ?? false,
  };
  if (input.durationMs !== undefined) outcomeBody.duration_ms = input.durationMs;

  /**
   * Swallows everything: a rejected request, a synchronous throw, and a transport that
   * returns a non-promise. Reporting hangs off a sync loop, so throwing into the caller
   * would take the sync down with it — strictly worse than not reporting at all.
   */
  const send = async (url: string, init: RequestInit): Promise<void> => {
    try {
      await fetchWithAuth(url, init);
    } catch {
      /* reporting is best-effort */
    }
  };

  const requests: Promise<void>[] = [];

  if (input.recordLedger !== false) {
    requests.push(
      send(`${base}/credential-outcome`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(outcomeBody),
      }),
    );
  }

  if (input.success) {
    const markBody: Record<string, unknown> = {};
    if (input.durationMs !== undefined) markBody.duration_ms = input.durationMs;
    requests.push(
      send(`${base}/mark-synced`, {
        method: 'PATCH',
        headers: jsonHeaders,
        body: JSON.stringify(markBody),
      }),
    );
  }

  await Promise.all(requests);

  // Tell every view that answers "is this person on that reader" to re-read. They each
  // hold their own snapshot, so without this the Permissões panels — and the Dispositivos
  // de Pessoas page — keep showing the state from BEFORE this sync, which is how the same
  // readers once appeared as "Removido do dispositivo" and "Confirmado no dispositivo" at
  // the same time.
  //
  // Announced even when the report failed to reach the backend: the DEVICE may well have
  // changed, and leaving every screen on a snapshot we know could be stale is worse.
  announceDeviceCredentialChange();
}
